/**
 * voiceCache — un vocal de la messagerie copié dans le cache de l'appareil, pour que le lecteur
 * natif le lise.
 *
 * `expo-audio` ne lit pas la route audio de la messagerie en flux (pièce jointe, sans `Range`), et
 * son téléchargement intégré force une mauvaise extension. Le téléchargement est celui de l'ancien
 * lecteur (`data/utility/downloadToCache`, sous le même nom de cache, donc partagé avec lui) : une
 * fois, partagé entre deux demandes simultanées, relu du disque ensuite.
 *
 * Sur Android, un vocal MP4 fragmenté sans index (celui d'un tuteur enregistré par son navigateur)
 * est copié une fois de plus, avec son index (`seekableVoice`) : sans lui, ExoPlayer repart de 0:00 à
 * chaque déplacement du curseur. L'original reste lu tel quel quand il n'en a pas besoin, ou si
 * l'indexation échoue.
 */
import { File } from "expo-file-system"
import { Platform } from "react-native"

import { downloadToCache } from "../../../../../../data/utility/downloadToCache"
import { createTelemetry } from "../../../../../../engine/shared/telemetry"
import { voiceExtension } from "./conversationVoice"
import { indexedFragmentedMp4 } from "./seekableVoice"

const telemetry = createTelemetry("voice-cache", { role: "student" })

export async function cachedVoiceUri(url: string): Promise<string> {
    const extension = voiceExtension(url)
    const uri = (await downloadToCache(url, { prefix: "messenger-audio-", extension })) as string
    if (Platform.OS !== "android" || extension !== ".mp4") return uri
    return seekableCopy(uri)
}

/** La copie indexée du fichier en cache, faite une fois ; l'original quand il n'en a pas besoin. */
async function seekableCopy(uri: string): Promise<string> {
    const indexedUri = uri.replace(/\.mp4$/, ".seekable.mp4")
    try {
        const indexed = new File(indexedUri)
        if (indexed.exists) return indexedUri
        const bytes = indexedFragmentedMp4(await new File(uri).bytes())
        if (bytes == null) return uri
        // Écrit à côté, puis déplacé : une app tuée pendant l'écriture ne laisse pas une copie
        // tronquée que le prochain lancement prendrait pour la bonne (`indexed.exists`).
        const partial = new File(`${indexedUri}.tmp`)
        if (partial.exists) partial.delete()
        partial.write(bytes)
        partial.move(indexed)
        return indexedUri
    } catch (cause) {
        // Le vocal se lit quand même, sans curseur sur Android : ce qu'il faisait avant.
        telemetry.error("index", cause)
        return uri
    }
}
